export type GoalState = 'on_track' | 'achieved' | 'off_track';

export function getMultiGoalStatus(states: GoalState[]) {
  const total = states.length;
  const onTrackCount = states.filter((s) => s === 'on_track' || s === 'achieved').length;
  const achievedCount = states.filter((s) => s === 'achieved').length;

  if (total === 0) {
    return { label: 'Snapshot', color: 'gray' as const };
  }

  if (achievedCount === total) {
    return { label: 'Achieved', color: 'green' as const };
  }

  if (onTrackCount === total) {
    return { label: 'On track', color: 'green' as const };
  }

  if (onTrackCount === 0) {
    return { label: 'At risk', color: 'red' as const, tooltip: 'All goals are more than 10% behind goal.' };
  }

  return { label: 'Watch', color: 'yellow' as const, tooltip: 'Some goals are on track.' };
}

type PaceDirection = 'min' | 'max';

const parseQuarterLabel = (label: string): { year: number; quarter: number } | null => {
  const m1 = label.match(/^Q([1-4])\s+(\d{4})$/);
  if (m1) return { year: Number(m1[2]), quarter: Number(m1[1]) };
  const m2 = label.match(/^(\d{4})\s+Q([1-4])$/);
  if (m2) return { year: Number(m2[1]), quarter: Number(m2[2]) };
  return null;
};

export function parseQuarterToken(label: string): { year: number; quarter: number } | null {
  const spaced = parseQuarterLabel(label);
  if (spaced) return spaced;
  const compact = label.match(/^(\d{4})-Q([1-4])$/);
  if (compact) return { year: Number(compact[1]), quarter: Number(compact[2]) };
  return null;
}

function quarterIndex(year: number, quarter: number): number {
  return year * 4 + (quarter - 1);
}

export function isQuarterInPaceWindow(
  quarterLabel: string,
  paceWindow?: { startQuarter?: string; endQuarter?: string } | null
): boolean {
  if (!paceWindow?.startQuarter || !paceWindow?.endQuarter) return false;
  const current = parseQuarterToken(quarterLabel);
  const start = parseQuarterToken(paceWindow.startQuarter);
  const end = parseQuarterToken(paceWindow.endQuarter);
  if (!current || !start || !end) return false;
  const idx = quarterIndex(current.year, current.quarter);
  return idx >= quarterIndex(start.year, start.quarter) && idx <= quarterIndex(end.year, end.quarter);
}

export function resolveActiveGoal<T extends { grain?: string; periodKey?: string }>(
  goals: T[] | undefined,
  opts: { grain: string; year?: number }
): T | undefined {
  const list = goals ?? [];
  if (opts.grain === 'annual') {
    if (opts.year == null) return undefined;
    return list.find((goal) => goal.grain === 'annual' && goal.periodKey === String(opts.year));
  }
  return list.find((goal) => goal.grain === opts.grain);
}

export function targetNumber(
  goal: { targets?: Record<string, { value?: number; target?: number } | undefined> } | undefined,
  field: string
): number | undefined {
  const target = goal?.targets?.[field];
  if (!target) return undefined;
  if (typeof target.value === 'number') return target.value;
  if (typeof target.target === 'number') return target.target;
  return undefined;
}

export function targetRange(
  goal: { targets?: Record<string, { min?: number; max?: number } | undefined> } | undefined,
  field: string
): { min: number; max: number } | undefined {
  const target = goal?.targets?.[field];
  if (typeof target?.min !== 'number' || typeof target?.max !== 'number') return undefined;
  return { min: target.min, max: target.max };
}

export function getAllTimePaceStatus({
  value,
  goal,
  quarterLabel,
  paceWindow,
  direction = 'min',
  tolerance = 0.1,
}: {
  value: number;
  goal: number;
  quarterLabel: string;
  paceWindow?: { startQuarter?: string; endQuarter?: string } | null;
  direction?: PaceDirection;
  tolerance?: number;
}) {
  const current = parseQuarterToken(quarterLabel);
  const start = paceWindow ? parseQuarterToken(paceWindow.startQuarter ?? '') : null;
  const end = paceWindow ? parseQuarterToken(paceWindow.endQuarter ?? '') : null;
  if (!current || !start || !end || goal <= 0) return { label: 'Snapshot', color: 'gray' as const };

  const curIdx = quarterIndex(current.year, current.quarter);
  const startIdx = quarterIndex(start.year, start.quarter);
  const endIdx = quarterIndex(end.year, end.quarter);
  const total = endIdx - startIdx + 1;
  if (total <= 0) return { label: 'Snapshot', color: 'gray' as const };
  if (curIdx < startIdx) return { label: 'Snapshot', color: 'gray' as const };

  const elapsed = Math.min(curIdx, endIdx) - startIdx + 1;
  const expected = (goal * elapsed) / total;
  const margin = expected * tolerance;

  if (direction === 'min') {
    if (value >= goal) return { label: 'Achieved', color: 'green' as const };
    if (value >= expected) return { label: 'On track', color: 'green' as const };
    if (value >= expected - margin) return { label: 'Watch', color: 'yellow' as const };
    return { label: 'At risk', color: 'red' as const };
  }

  if (value <= goal) return { label: 'Achieved', color: 'green' as const };
  if (value <= expected) return { label: 'On track', color: 'green' as const };
  if (value <= expected + margin) return { label: 'Watch', color: 'yellow' as const };
  return { label: 'At risk', color: 'red' as const };
}

export function scoreAllTimeCountGoal({
  value,
  goals,
  field,
  quarterLabel,
  direction = 'min',
}: {
  value: number;
  goals: Array<{ grain?: string; periodKey?: string; targets?: Record<string, { value?: number; target?: number }>; paceWindow?: { startQuarter?: string; endQuarter?: string } }> | undefined;
  field: string;
  quarterLabel: string;
  direction?: PaceDirection;
}) {
  const goal = resolveActiveGoal(goals, { grain: 'all_time' });
  const target = targetNumber(goal, field);
  if (target == null) {
    return {
      label: 'Snapshot' as const,
      color: 'gray' as const,
      goal: undefined as number | undefined,
      tooltip: undefined as string | undefined,
    };
  }
  return {
    ...getAllTimePaceStatus({
      value,
      goal: target,
      quarterLabel,
      paceWindow: goal && 'paceWindow' in goal ? goal.paceWindow : undefined,
      direction,
    }),
    goal: target,
    tooltip: undefined as string | undefined,
  };
}

export function scoreAnnualCountGoal({
  ytd,
  goals,
  field,
  year,
  quarterLabel,
  direction = 'min',
}: {
  ytd: number;
  goals: Array<{ grain?: string; periodKey?: string; targets?: Record<string, { value?: number; target?: number }> }> | undefined;
  field: string;
  year?: number;
  quarterLabel: string;
  direction?: PaceDirection;
}) {
  const goal = resolveActiveGoal(goals, { grain: 'annual', year });
  const target = targetNumber(goal, field);
  if (target == null) {
    return {
      label: 'Snapshot' as const,
      color: 'gray' as const,
      goal: undefined as number | undefined,
      tooltip: undefined as string | undefined,
    };
  }
  return {
    ...getAnnualPaceStatus({ ytd, goalPerYear: target, quarterLabel, direction }),
    goal: target,
    tooltip: undefined as string | undefined,
  };
}

export function getAllTimePaceState(args: {
  value: number;
  goal: number;
  quarterLabel: string;
  paceWindow?: { startQuarter?: string; endQuarter?: string } | null;
  direction?: PaceDirection;
  tolerance?: number;
}): GoalState {
  const status = getAllTimePaceStatus(args);
  if (status.label === 'Achieved') return 'achieved';
  if (status.label === 'On track' || status.label === 'Watch') return 'on_track';
  if (status.label === 'At risk') return 'off_track';
  return 'off_track';
}

export function getAnnualPaceStatus({
  ytd,
  goalPerYear,
  quarterLabel,
  direction = 'min',
  tolerance = 0.1,
}: {
  ytd: number;
  goalPerYear: number;
  quarterLabel: string;
  direction?: PaceDirection;
  tolerance?: number;
}) {
  const meta = parseQuarterLabel(quarterLabel);
  if (!meta || goalPerYear <= 0) return { label: 'Snapshot', color: 'gray' as const };

  const expected = (goalPerYear * meta.quarter) / 4;
  const margin = expected * tolerance;

  if (direction === 'min') {
    if (ytd >= goalPerYear) return { label: 'Achieved', color: 'green' as const };
    if (ytd >= expected) return { label: 'On track', color: 'green' as const };
    if (ytd >= expected - margin) return { label: 'Watch', color: 'yellow' as const };
    return { label: 'At risk', color: 'red' as const };
  }

  if (ytd <= goalPerYear) return { label: 'Achieved', color: 'green' as const };
  if (ytd <= expected) return { label: 'On track', color: 'green' as const };
  if (ytd <= expected + margin) return { label: 'Watch', color: 'yellow' as const };
  return { label: 'At risk', color: 'red' as const };
}

export function getAnnualPaceState({
  ytd,
  goalPerYear,
  quarterLabel,
  direction = 'min',
  tolerance = 0.1,
}: {
  ytd: number;
  goalPerYear: number;
  quarterLabel: string;
  direction?: PaceDirection;
  tolerance?: number;
}): GoalState {
  const meta = parseQuarterLabel(quarterLabel);
  if (!meta || goalPerYear <= 0) return 'off_track';

  const expected = (goalPerYear * meta.quarter) / 4;
  const margin = expected * tolerance;

  if (direction === 'min') {
    if (ytd >= goalPerYear) return 'achieved';
    if (ytd >= expected - margin) return 'on_track';
    return 'off_track';
  }

  if (ytd <= goalPerYear) return 'achieved';
  if (ytd <= expected + margin) return 'on_track';
  return 'off_track';
}

export function getThresholdGoalState({
  value,
  goal,
  direction = 'min',
  tolerance = 0.1,
}: {
  value: number | null;
  goal: number;
  direction?: PaceDirection;
  tolerance?: number;
}): GoalState {
  if (value === null) return 'off_track';
  const margin = goal * tolerance;

  if (direction === 'min') {
    if (value >= goal) return 'achieved';
    if (value >= goal - margin) return 'on_track';
    return 'off_track';
  }

  if (value <= goal) return 'achieved';
  if (value <= goal + margin) return 'on_track';
  return 'off_track';
}

export function getRangeGoalState({
  value,
  min,
  max,
  tolerance = 0.1,
}: {
  value: number | null;
  min: number;
  max: number;
  tolerance?: number;
}): GoalState {
  if (value === null) return 'off_track';
  const margin = min * tolerance;
  if (value >= max) return 'achieved';
  if (value >= min - margin) return 'on_track';
  return 'off_track';
}

export function buildAnnualGoalPaceTooltipLine({
  metricLabel,
  ytd,
  goalPerYear,
  quarterLabel,
  direction = 'min',
}: {
  metricLabel: string;
  ytd: number;
  goalPerYear: number;
  quarterLabel: string;
  direction?: PaceDirection;
}): string {
  const meta = parseQuarterLabel(quarterLabel);
  if (!meta || goalPerYear <= 0) {
    return `${metricLabel}: no annual goal configured.`;
  }
  const expected = (goalPerYear * meta.quarter) / 4;
  const pace = getAnnualPaceStatus({ ytd, goalPerYear, quarterLabel, direction });
  return `${metricLabel}: ${ytd} calendar year to date (${pace.label}; ~${Math.round(expected)} expected through Q${meta.quarter}, goal ${goalPerYear}/year)`;
}

export function buildMultiAnnualGoalStatusTooltip({
  quarterLabel,
  metrics,
  summaryTooltip,
}: {
  quarterLabel: string;
  metrics: Array<{ label: string; ytd: number; goalPerYear?: number; direction?: PaceDirection }>;
  summaryTooltip?: string;
}): string | undefined {
  const lines = metrics
    .filter((metric) => metric.goalPerYear !== undefined)
    .map((metric) =>
      buildAnnualGoalPaceTooltipLine({
        metricLabel: metric.label,
        ytd: metric.ytd,
        goalPerYear: metric.goalPerYear!,
        quarterLabel,
        direction: metric.direction,
      })
    );

  if (lines.length === 0) return undefined;

  if (summaryTooltip) {
    return `${summaryTooltip}\n\n${lines.join('\n')}`;
  }
  return lines.join('\n');
}
